import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useState, useEffect } from "react";
import StripePaymentForm from "./StripePaymentForm";
import { supabase } from "@/integrations/supabase/client";

const VITE_STRIPE_PUBLIC_KEY = import.meta.env.VITE_STRIPE_PUBLIC_KEY || "pk_test_placeholder";
const stripePromise = loadStripe(VITE_STRIPE_PUBLIC_KEY);

interface StripePaymentWrapperProps {
 orderId?: string;
 streamId?: string;
 isSubscription?: boolean;
 amount?: number;
 planId?: string;
 onSuccess: () => void;
 onError: (error: string) => void;
}

export default function StripePaymentWrapper({ orderId, streamId, isSubscription, amount, planId, onSuccess, onError }: StripePaymentWrapperProps) {
 const [clientSecret, setClientSecret] = useState<string | null>(null);
 const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 async function fetchPaymentIntent() {
 try {
 const functionName = isSubscription ? "create-stripe-subscription-intent" : "create-stripe-payment-intent";
 const { data, error } = await supabase.functions.invoke(functionName, {
 body: { order_id: orderId, stream_id: streamId, amount: amount, plan_id: planId },
 });

 if (error) {
 throw new Error("Erro na comunicação com o servidor de pagamentos.");
 }

 if (!isSubscription && data.free) {
 // If the amount is 0 (free content), auto-succeed
 onSuccess();
 return;
 }

 if (data.error) {
 throw new Error(data.error);
 }

 setClientSecret(data.clientSecret);
 if (data.subscriptionId) {
 setSubscriptionId(data.subscriptionId);
 }
 } catch (err: any) {
 console.error("Stripe Intent Error:", err);
 onError(err.message || "Erro ao inicializar o pagamento.");
 } finally {
 setLoading(false);
 }
 }

 fetchPaymentIntent();
 }, [orderId, streamId, isSubscription]);

 if (loading) {
 return (
 <div className="py-8 text-center text-muted-foreground">
 <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
 Preparando ambiente seguro de pagamento...
 </div>
 );
 }

 if (!clientSecret) {
 return (
 <div className="py-8 text-center text-red-500">
 Não foi possível carregar o formulário de pagamento.
 </div>
 );
 }

 return (
 <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'night' } }}>
 <StripePaymentForm onSuccess={onSuccess} onError={onError} subscriptionId={subscriptionId} />
 </Elements>
 );
}
